import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Switch,
  FormControlLabel,
  Typography,
  CircularProgress,
  Grid,
  Alert,
  Chip,
  Avatar
} from "@mui/material";
import { Google, CheckCircle, Link as LinkIcon } from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { IN8NWorkflow, ICreateN8NWorkflowRequest, N8NWorkflowType } from "../../../../models/n8n-workflow";
import useAxios from "../../../../components/UseAxios/useAxios";
import N8NWorkflowService from "../../../../services/api/n8n-workflow.service";
import GoogleOAuthService, { IGoogleConnection } from "../../../../services/api/google-oauth.service";
import { toggleMessage } from "../../../../components/Toast/Toast";
import { useSearchParams } from "react-router-dom";

interface WorkflowFormProps {
  workflow: IN8NWorkflow | null;
  editMode: boolean;
  onSuccess: () => void;
  onCancel: () => void;
}

// Helper function to safely get error messages
const getErrorMessage = (error: any): string | undefined => {
  if (!error) return undefined;
  if (typeof error === 'string') return error;
  if (typeof error.message === 'string') return error.message;
  return undefined;
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  type: Yup.string().required("Type is required"),
  status: Yup.string().oneOf(['active', 'inactive']).required(),
  googleSheetUrl: Yup.string().url("Invalid URL").required("Google Sheet URL is required"),
  googleClientId: Yup.string().required("Google Client ID is required"),
  googleClientSecret: Yup.string().required("Google Client Secret is required"),
  googleSheetsCredentialId: Yup.string(), // Optional - will be set after OAuth
  aiModel: Yup.string().required("AI Model is required"),
  aiApiKey: Yup.string().required("AI API Key is required"),
  facebookPageId: Yup.string().when('type', {
    is: (val: string) => val === N8NWorkflowType.FACEBOOK_REPLY || val === N8NWorkflowType.SHEET_PROCESSOR,
    then: (schema) => schema.required("Facebook Page ID is required")
  }),
  facebookAccessToken: Yup.string().when('type', {
    is: (val: string) => val === N8NWorkflowType.FACEBOOK_REPLY || val === N8NWorkflowType.SHEET_PROCESSOR,
    then: (schema) => schema.required("Facebook Access Token is required")
  }),
  websiteUrl: Yup.string().when('type', {
    is: (val: string) => val === N8NWorkflowType.WEBSITE_SCRAPER || val === N8NWorkflowType.WEBSITE_POSTER,
    then: (schema) => schema.url("Invalid URL").required("Website URL is required")
  }),
  websiteApiKey: Yup.string().when('type', {
    is: (val: string) => val === N8NWorkflowType.WEBSITE_SCRAPER,
    then: (schema) => schema.required("Website API Key is required when type is Website Scraper")
  }),
  websiteUsername: Yup.string().when('type', {
    is: (val: string) => val === N8NWorkflowType.WEBSITE_POSTER,
    then: (schema) => schema.required("WordPress Username is required for Website Poster")
  }),
  websitePassword: Yup.string().when('type', {
    is: (val: string) => val === N8NWorkflowType.WEBSITE_POSTER,
    then: (schema) => schema.required("WordPress App Password is required for Website Poster")
  }),
  checkInterval: Yup.number().min(1, "Check interval must be at least 1 minute").required("Check interval is required"),
});

const WorkflowForm: React.FC<WorkflowFormProps> = ({
  workflow,
  editMode,
  onSuccess,
  onCancel
}) => {
  const [searchParams] = useSearchParams();
  const createWorkflowAxios = useAxios<IN8NWorkflow>({ loading: "OnRequest" });
  const updateWorkflowAxios = useAxios<IN8NWorkflow>({ loading: "OnRequest" });

  // Google OAuth state
  const [googleConnections, setGoogleConnections] = useState<IGoogleConnection[]>([]);
  const [isLoadingConnections, setIsLoadingConnections] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  const { control, handleSubmit, watch, reset, setValue, getValues, formState: { errors } } = useForm<any>({
    defaultValues: {
      name: workflow?.name || "",
      type: workflow?.type || "",
      status: workflow?.status || 'active',
      googleSheetUrl: workflow?.googleSheetUrl || "",
      googleClientId: "",
      googleClientSecret: "",
      googleSheetsCredentialId: "",
      aiModel: workflow?.aiModel || "gpt-4",
      aiApiKey: "",
      facebookPageId: workflow?.facebookPageId || "",
      facebookAccessToken: "",
      websiteUrl: workflow?.websiteUrl || "",
      websiteApiKey: "",
      websiteUsername: "",
      websitePassword: "",
      checkInterval: workflow?.checkInterval || 300,
    },
    resolver: yupResolver(validationSchema)
  });

  const watchedType = watch("type");
  const watchedStatus = watch("status");
  const watchedCredentialId = watch("googleSheetsCredentialId");

  // Load Google connections on mount
  useEffect(() => {
    loadGoogleConnections();
  }, []);

  // Check for OAuth callback in URL
  useEffect(() => {
    const oauthStatus = searchParams.get('oauth');
    if (oauthStatus === 'success') {
      toggleMessage({ type: "success", message: "Google account connected successfully!" });
      loadGoogleConnections();
    } else if (oauthStatus === 'error') {
      const message = searchParams.get('message') || 'Failed to connect Google account';
      toggleMessage({ type: "error", message });
    }
  }, [searchParams]);

  const loadGoogleConnections = async () => {
    setIsLoadingConnections(true);
    try {
      const response = await GoogleOAuthService.getConnections();
      if (response.data) {
        setGoogleConnections(response.data);
        // Auto-select first connection if available and not already selected
        if (response.data.length > 0 && !watchedCredentialId) {
          setValue('googleSheetsCredentialId', response.data[0].n8nCredentialId);
        }
      }
    } catch (error) {
      console.error('Failed to load Google connections:', error);
    } finally {
      setIsLoadingConnections(false);
    }
  };

  const handleConnectGoogle = async () => {
    const clientId = getValues('googleClientId');
    const clientSecret = getValues('googleClientSecret');

    if (!clientId || !clientSecret) {
      toggleMessage({ type: "error", message: "Please enter Google Client ID and Client Secret first" });
      return;
    }

    setIsConnecting(true);
    try {
      const response = await GoogleOAuthService.getAuthUrl(clientId, clientSecret) as any;
      if (response?.authUrl) {
        // Open OAuth popup
        const popup = window.open(
          response.authUrl,
          'google-oauth',
          'width=500,height=600,scrollbars=yes'
        );

        // Poll for popup close
        const checkClosed = setInterval(() => {
          if (popup?.closed) {
            clearInterval(checkClosed);
            setIsConnecting(false);
            // Reload connections after popup closes
            setTimeout(() => loadGoogleConnections(), 1000);
          }
        }, 500);
      } else {
        toggleMessage({ type: "error", message: response?.message || "Failed to get OAuth URL" });
        setIsConnecting(false);
      }
    } catch (error: any) {
      toggleMessage({ type: "error", message: error.message || "Failed to initiate Google connection" });
      setIsConnecting(false);
    }
  };

  const onSubmit = (data: any) => {
    const request: ICreateN8NWorkflowRequest = {
      name: data.name,
      description: "", // Auto-filled
      type: data.type,
      status: data.status,
      documentUrl: "", // Auto-filled from business
      sheetUrl: "", // Auto-filled from business
      googleSheetUrl: data.googleSheetUrl,
      googleClientId: data.googleClientId,
      googleClientSecret: data.googleClientSecret,
      googleSheetsCredentialId: data.googleSheetsCredentialId,
      aiModel: data.aiModel,
      aiApiKey: data.aiApiKey,
      aiPromptTemplate: "", // Backend will use default
      facebookPageId: data.facebookPageId,
      facebookAccessToken: data.facebookAccessToken,
      websiteUrl: data.websiteUrl,
      websiteApiKey: data.websiteApiKey,
      websiteUsername: data.websiteUsername,
      websitePassword: data.websitePassword,
      checkInterval: Number(data.checkInterval),
    };

    if (editMode && workflow) {
      updateWorkflowAxios.request(
        N8NWorkflowService.updateWorkflow(workflow._id, request)
      );
    } else {
      createWorkflowAxios.request(
        N8NWorkflowService.createWorkflow(request)
      );
    }
  };

  useEffect(() => {
    if (createWorkflowAxios.isSuccess) {
      toggleMessage({ type: "success", message: "Workflow created successfully!" });
      reset();
      onSuccess();
    }
    if (createWorkflowAxios.error) {
      toggleMessage({
        type: "error",
        message: createWorkflowAxios.error?.message || "Failed to create workflow"
      });
    }
  }, [createWorkflowAxios.isSuccess, createWorkflowAxios.error]);

  useEffect(() => {
    if (updateWorkflowAxios.isSuccess) {
      toggleMessage({ type: "success", message: "Workflow updated successfully!" });
      onSuccess();
    }
    if (updateWorkflowAxios.error) {
      toggleMessage({
        type: "error",
        message: updateWorkflowAxios.error?.message || "Failed to update workflow"
      });
    }
  }, [updateWorkflowAxios.isSuccess, updateWorkflowAxios.error]);

  const isLoading = createWorkflowAxios.isLoading || updateWorkflowAxios.isLoading;

  // Get selected connection info
  const selectedConnection = googleConnections.find(c => c.n8nCredentialId === watchedCredentialId);

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ pt: 4 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

        {/* Row 1: Name & Type */}
        <Grid container spacing={2}>
          {/* Name */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Workflow Name *"
                  fullWidth
                  variant="outlined"
                  size="small"
                  error={!!errors.name}
                  helperText={getErrorMessage(errors.name)}
                />
              )}
            />
          </Grid>

          {/* Type */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.type} size="small">
                  <InputLabel>Workflow Type *</InputLabel>
                  <Select {...field} label="Workflow Type *">
                    {/* <MenuItem value={N8NWorkflowType.FACEBOOK_REPLY}>Facebook Reply</MenuItem>
                    <MenuItem value={N8NWorkflowType.WEBSITE_SCRAPER}>Website Scraper</MenuItem> */}
                    <MenuItem value={N8NWorkflowType.SHEET_PROCESSOR}>Sheet Processor</MenuItem>
                    <MenuItem value={N8NWorkflowType.WEBSITE_POSTER}>Website Poster</MenuItem>
                  </Select>
                  {errors.type && <FormHelperText>{getErrorMessage(errors.type)}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>
        </Grid>

        {/* Status */}
        <Box>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={field.value === 'active'}
                    onChange={(e) => field.onChange(e.target.checked ? 'active' : 'inactive')}
                  />
                }
                label={`Status: ${watchedStatus === 'active' ? 'Active' : 'Inactive'}`}
              />
            )}
          />
        </Box>

        {/* ===== GOOGLE SHEET CONFIGURATION ===== */}
        <Box sx={{ pt: 1, pb: 1, fontWeight: 600, fontSize: '0.9rem', color: '#666' }}>
          📊 Google Sheet Configuration
        </Box>

        {/* Google OAuth Credentials */}
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2, bgcolor: '#fff3e0' }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Google OAuth Credentials
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Get these from Google Cloud Console → APIs & Services → Credentials → OAuth 2.0 Client IDs
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="googleClientId"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Google Client ID *"
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="xxx.apps.googleusercontent.com"
                    error={!!errors.googleClientId}
                    helperText={getErrorMessage(errors.googleClientId)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="googleClientSecret"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Google Client Secret *"
                    fullWidth
                    variant="outlined"
                    size="small"
                    type="password"
                    error={!!errors.googleClientSecret}
                    helperText={getErrorMessage(errors.googleClientSecret)}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Google Account Connection */}
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2, bgcolor: '#fafafa' }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Google Account Connection
          </Typography>

          {isLoadingConnections ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">Loading connections...</Typography>
            </Box>
          ) : googleConnections.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Connection Selector */}
              <Controller
                name="googleSheetsCredentialId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.googleSheetsCredentialId} size="small">
                    <InputLabel>Select Google Account *</InputLabel>
                    <Select {...field} label="Select Google Account *">
                      {googleConnections.map((conn) => (
                        <MenuItem key={conn.id} value={conn.n8nCredentialId}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar src={conn.picture} sx={{ width: 24, height: 24 }}>
                              {conn.name?.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2">{conn.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{conn.email}</Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.googleSheetsCredentialId && (
                      <FormHelperText>{getErrorMessage(errors.googleSheetsCredentialId)}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />

              {/* Selected Account Info */}
              {selectedConnection && (
                <Chip
                  icon={<CheckCircle sx={{ color: '#4caf50 !important' }} />}
                  label={`Connected: ${selectedConnection.email}`}
                  color="success"
                  variant="outlined"
                  size="small"
                />
              )}

              {/* Connect Another Button */}
              <Button
                variant="outlined"
                size="small"
                startIcon={isConnecting ? <CircularProgress size={16} /> : <Google />}
                onClick={handleConnectGoogle}
                disabled={isConnecting}
                sx={{ alignSelf: 'flex-start' }}
              >
                {isConnecting ? 'Connecting...' : 'Connect Another Account'}
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
              <Alert severity="info" sx={{ width: '100%' }}>
                No Google account connected. Click below to connect your Google account for Google Sheets access.
              </Alert>
              <Button
                variant="contained"
                startIcon={isConnecting ? <CircularProgress size={16} color="inherit" /> : <Google />}
                onClick={handleConnectGoogle}
                disabled={isConnecting}
                sx={{
                  bgcolor: '#4285f4',
                  '&:hover': { bgcolor: '#3367d6' }
                }}
              >
                {isConnecting ? 'Connecting...' : 'Connect Google Account'}
              </Button>
              {errors.googleSheetsCredentialId && (
                <Typography variant="caption" color="error">
                  {getErrorMessage(errors.googleSheetsCredentialId)}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Row: Google Sheet URL */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name="googleSheetUrl"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Google Sheet URL *"
                  fullWidth
                  variant="outlined"
                  size="small"
                  type="url"
                  error={!!errors.googleSheetUrl}
                  helperText={getErrorMessage(errors.googleSheetUrl) || "https://docs.google.com/spreadsheets/d/..."}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* ===== AI CONFIGURATION ===== */}
        <Box sx={{ pt: 2, pb: 1, fontWeight: 600, fontSize: '0.9rem', color: '#666' }}>
          🤖 AI Configuration
        </Box>

        {/* Row: AI Model & API Key */}
        <Grid container spacing={2}>
          {/* AI Model */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="aiModel"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="AI Model *"
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="e.g., gpt-4, gpt-4o-mini"
                  error={!!errors.aiModel}
                  helperText={getErrorMessage(errors.aiModel)}
                />
              )}
            />
          </Grid>

          {/* AI API Key */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="aiApiKey"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="AI API Key *"
                  fullWidth
                  variant="outlined"
                  size="small"
                  type="password"
                  error={!!errors.aiApiKey}
                  helperText={getErrorMessage(errors.aiApiKey)}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* Conditional: Facebook Fields */}
        {(watchedType === N8NWorkflowType.FACEBOOK_REPLY || watchedType === N8NWorkflowType.SHEET_PROCESSOR) && (
          <>
            <Box sx={{ pt: 2, pb: 1, fontWeight: 600, fontSize: '0.9rem', color: '#666' }}>
              📱 Facebook Configuration
            </Box>
            <Grid container spacing={2}>
              {/* Facebook Page ID */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="facebookPageId"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Facebook Page ID *"
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="e.g., 123456789"
                      error={!!errors.facebookPageId}
                      helperText={getErrorMessage(errors.facebookPageId)}
                    />
                  )}
                />
              </Grid>

              {/* Facebook Access Token */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="facebookAccessToken"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Facebook Access Token *"
                      fullWidth
                      variant="outlined"
                      size="small"
                      type="password"
                      error={!!errors.facebookAccessToken}
                      helperText={getErrorMessage(errors.facebookAccessToken)}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </>
        )}

        {/* Conditional: Website Fields */}
        {watchedType === N8NWorkflowType.WEBSITE_SCRAPER && (
          <>
            <Box sx={{ pt: 2, pb: 1, fontWeight: 600, fontSize: '0.9rem', color: '#666' }}>
              🌐 Website Configuration
            </Box>
            <Grid container spacing={2}>
              {/* Website URL */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="websiteUrl"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Website URL *"
                      fullWidth
                      variant="outlined"
                      size="small"
                      type="url"
                      error={!!errors.websiteUrl}
                      helperText={getErrorMessage(errors.websiteUrl)}
                    />
                  )}
                />
              </Grid>

              {/* Website API Key */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="websiteApiKey"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Website API Key *"
                      fullWidth
                      variant="outlined"
                      size="small"
                      type="password"
                      error={!!errors.websiteApiKey}
                      helperText={getErrorMessage(errors.websiteApiKey)}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </>
        )}

        {/* Conditional: Website Poster Fields */}
        {watchedType === N8NWorkflowType.WEBSITE_POSTER && (
          <>
            <Box sx={{ pt: 2, pb: 1, fontWeight: 600, fontSize: '0.9rem', color: '#666' }}>
              🌐 WordPress Configuration
            </Box>
            <Grid container spacing={2}>
              {/* Website URL */}
              <Grid item xs={12}>
                <Controller
                  name="websiteUrl"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="WordPress REST API URL *"
                      fullWidth
                      variant="outlined"
                      size="small"
                      type="url"
                      placeholder="https://example.com/wp-json/wp/v2/posts"
                      error={!!errors.websiteUrl}
                      helperText={getErrorMessage(errors.websiteUrl) || "Full URL to WordPress REST API posts endpoint"}
                    />
                  )}
                />
              </Grid>

              {/* WordPress Username */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="websiteUsername"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="WordPress Username *"
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="admin"
                      error={!!errors.websiteUsername}
                      helperText={getErrorMessage(errors.websiteUsername)}
                    />
                  )}
                />
              </Grid>

              {/* WordPress App Password */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="websitePassword"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="WordPress App Password *"
                      fullWidth
                      variant="outlined"
                      size="small"
                      type="password"
                      placeholder="xxxx xxxx xxxx xxxx"
                      error={!!errors.websitePassword}
                      helperText={getErrorMessage(errors.websitePassword) || "Create at Users → Profile → Application Passwords"}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </>
        )}

        {/* Check Interval */}
        <Box sx={{ pt: 2, pb: 1, fontWeight: 600, fontSize: '0.9rem', color: '#666' }}>
          ⏱️ Execution Settings
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="checkInterval"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Check Interval (seconds) *"
                  fullWidth
                  variant="outlined"
                  size="small"
                  type="number"
                  error={!!errors.checkInterval}
                  helperText={getErrorMessage(errors.checkInterval) || "e.g., 300 = 5 minutes"}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* Sheet Processor Info */}
        {watchedType === N8NWorkflowType.SHEET_PROCESSOR && (
          <Box sx={{ mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
            <Typography variant="caption" color="primary" sx={{ display: 'block', lineHeight: 1.6, fontWeight: 500 }}>
              <strong>📋 Sheet Processor Requirements:</strong>
              <br />
              Your Google Sheet must have these columns:
              <br />
              <strong>STT | PROMPT | CONTENT | DATE | TIME | STATUS | CHECKED</strong>
              <br />
              The workflow will automatically read pending rows, generate content with AI, and update status.
            </Typography>
          </Box>
        )}

        {/* Website Poster Info */}
        {watchedType === N8NWorkflowType.WEBSITE_POSTER && (
          <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #a5d6a7' }}>
            <Typography variant="caption" color="success.dark" sx={{ display: 'block', lineHeight: 1.6, fontWeight: 500 }}>
              <strong>🌐 Website Poster Requirements:</strong>
              <br />
              Your Google Sheet must have these columns:
              <br />
              <strong>STT | PROMPT | AVATAR | TITLE | DESCRIPTION | CONTENT | DATE | TIME | STATUS | CHECKED</strong>
              <br />
              • <strong>AVATAR</strong>: WordPress Media ID for featured image (optional)
              <br />
              • <strong>TITLE, DESCRIPTION, CONTENT</strong>: Auto-generated by AI
              <br />
              The workflow will read pending rows, generate content with AI, and post to WordPress.
            </Typography>
          </Box>
        )}

        {/* Actions */}
        <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading
              ? editMode
                ? "Updating..."
                : "Creating..."
              : editMode
                ? "Update Workflow"
                : "Create Workflow"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default WorkflowForm;